// src/pages/admin/branches/components/BranchFormDialog.jsx

import { useTranslation } from 'react-i18next';
import { useRTL } from '../../../../hooks/useRTL';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    FormControlLabel,
    Switch,
    CircularProgress,
    Box,
    IconButton
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

const BranchFormDialog = ({
    open,
    onClose,
    formik,
    selectedBranch,
    loading
}) => {
    const { t } = useTranslation();
    const { isRTL } = useRTL();

    const isEditing = Boolean(selectedBranch);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            dir={isRTL ? 'rtl' : 'ltr'}
        >
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    {isEditing
                        ? t('branches.editTitle', 'Edit Branch')
                        : t('branches.addTitle', 'Add Branch')
                    }
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <form onSubmit={formik.handleSubmit}>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        {/* Branch Name */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                name="name"
                                label={t('branches.branchName', 'Branch Name')}
                                value={formik.values.name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.name && Boolean(formik.errors.name)}
                                helperText={formik.touched.name && formik.errors.name}
                                required
                            />
                        </Grid>

                        {/* Location */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                name="location"
                                label={t('branches.location', 'Location')}
                                value={formik.values.location}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.location && Boolean(formik.errors.location)}
                                helperText={formik.touched.location && formik.errors.location}
                                required
                            />
                        </Grid>

                        {/* Address */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                name="address"
                                label={t('branches.address', 'Address')}
                                value={formik.values.address}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.address && Boolean(formik.errors.address)}
                                helperText={formik.touched.address && formik.errors.address}
                                multiline
                                rows={2}
                            />
                        </Grid>

                        {/* Phone */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                name="phone"
                                label={t('branches.phone', 'Phone')}
                                value={formik.values.phone}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.phone && Boolean(formik.errors.phone)}
                                helperText={formik.touched.phone && formik.errors.phone}
                                placeholder="+201234567890"
                            />
                        </Grid>

                        {/* Manager */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                name="manager"
                                label={t('branches.manager', 'Manager')}
                                value={formik.values.manager}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.manager && Boolean(formik.errors.manager)}
                                helperText={formik.touched.manager && formik.errors.manager}
                            />
                        </Grid>

                        {/* Active Status */}
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        name="isActive"
                                        checked={formik.values.isActive}
                                        onChange={formik.handleChange}
                                        color="success"
                                    />
                                }
                                label={t('branches.isActive', 'Active')}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={onClose} disabled={loading}>
                        {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading || !formik.isValid}
                        startIcon={loading && <CircularProgress size={18} />}
                    >
                        {loading
                            ? t('common.saving', 'Saving...')
                            : isEditing
                                ? t('common.update', 'Update')
                                : t('common.create', 'Create')
                        }
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default BranchFormDialog;